// A bottom sheet on a phone, a centred card on a desk. Module scope on purpose
// (PLAYBOOK §17b: a component declared inside another remounts every render and
// a text field loses focus after one character).
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { overlayClickClose } from '../../../shared/overlay-close';

export function Sheet({
  title,
  onClose,
  children,
  label,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Accessible name for the dialog. */
  label: string;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-tcs-overlay p-4 shadow-xl sm:max-w-md sm:rounded-2xl"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-tcs-text">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-bg-muted hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A chip that toggles — markets, kinds of work, the status options. */
export function Chip({
  on,
  onClick,
  children,
  disabled,
  title,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition ${
        on
          ? 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export const bigButton =
  'mt-2 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40';
export const bigPrimary = `${bigButton} bg-tcs-inverse text-tcs-inverse-ink hover:bg-tcs-inverse-hover`;
export const bigSecondary = `${bigButton} border border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted`;
export const fieldCls =
  'w-full rounded-lg border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-border-input focus:outline-none';
